/**
 * Desfecho da evolução — detecção do cenário e tradução para cards de CRM.
 *
 * O médico não deve declarar de novo algo que já disse na evolução: se indicou
 * uma cirurgia, o cenário é cirurgia; se pediu exame, é exame. Este módulo
 * detecta o cenário a partir do que foi preenchido e define quais perguntas
 * ainda faltam — e só essas aparecem na tela.
 *
 * A classificação sai da natureza do ato declarada na categoria do procedimento
 * (`clinic_procedure_categories.clinical_act_type`), então funciona para qualquer
 * especialidade sem regra hardcoded. Quando não é possível classificar, o cenário
 * é `unclassified` e o médico escolhe — nunca adivinhamos.
 *
 * Módulo puro: sem React, sem rede.
 */

import type {
  ClinicalActType,
  EvolutionAttachedPrintable,
  EvolutionIndicationItem,
  EvolutionReturnWorkflow,
} from './evolutionWorkflowTypes';

// ---------------------------------------------------------------------------
// Duração
// ---------------------------------------------------------------------------

export type DurationUnit = 'days' | 'weeks' | 'months';

export interface Duration {
  value: number;
  unit: DurationUnit;
}

const DAYS_PER_UNIT: Record<DurationUnit, number> = { days: 1, weeks: 7, months: 30 };

export const DURATION_UNIT_LABEL: Record<DurationUnit, string> = {
  days: 'dias',
  weeks: 'semanas',
  months: 'meses',
};

export function toDays(duration?: Duration | null): number | null {
  if (!duration) return null;
  const value = Number(duration.value);
  if (!Number.isFinite(value) || value <= 0) return null;
  return Math.round(value * DAYS_PER_UNIT[duration.unit]);
}

/** Dias -> duração legível, preferindo a unidade que dá número inteiro. */
export function fromDays(days?: number | null): Duration | null {
  const total = Number(days);
  if (!Number.isFinite(total) || total <= 0) return null;
  if (total % 30 === 0) return { value: total / 30, unit: 'months' };
  if (total % 7 === 0) return { value: total / 7, unit: 'weeks' };
  return { value: total, unit: 'days' };
}

export function formatDuration(duration?: Duration | null): string {
  const days = toDays(duration);
  if (!days || !duration) return '';
  const plural = duration.value === 1;
  const unit = plural
    ? { days: 'dia', weeks: 'semana', months: 'mês' }[duration.unit]
    : DURATION_UNIT_LABEL[duration.unit];
  return `${duration.value} ${unit}`;
}

function isoAfterDays(days: number | null): string {
  const d = new Date();
  d.setDate(d.getDate() + Math.max(0, days ?? 0));
  return d.toISOString();
}

/** Data local (yyyy-MM-dd) a partir de hoje + N dias. */
export function localDateAfterDays(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + Math.max(0, days));
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function formatOutcomeReturnDate(dateIso: string): string {
  const [y, mo, da] = dateIso.split('-').map(Number);
  if (!y || !mo || !da) return dateIso;
  return new Date(y, mo - 1, da).toLocaleDateString('pt-BR');
}

export interface OutcomeSchedulingSuggestion {
  appointmentDate: string;
  appointmentType: 'return';
  summary: string;
}

/**
 * Desfechos com retorno agendável: retorno assistencial, volta ao médico com exame
 * e retorno pré-operatório antes da cirurgia.
 */
export function getOutcomeSchedulingSuggestion(
  outcome: EvolutionOutcome,
  detection: OutcomeDetection,
): OutcomeSchedulingSuggestion | null {
  const effective = resolveEffectiveOutcome(outcome, detection);

  if (effective.scenario === 'clinical' && effective.clinical.disposition === 'return') {
    const days = toDays(effective.clinical.returnIn);
    if (!days) return null;
    return {
      appointmentDate: localDateAfterDays(days),
      appointmentType: 'return',
      summary: `retorno assistencial em ${formatDuration(effective.clinical.returnIn)}`,
    };
  }

  if (effective.scenario === 'exam' && effective.exam.followUp === 'back_to_doctor') {
    const days = toDays(effective.exam.returnIn);
    if (!days) return null;
    return {
      appointmentDate: localDateAfterDays(days),
      appointmentType: 'return',
      summary: `volta ao médico com o exame em ${formatDuration(effective.exam.returnIn)}`,
    };
  }

  if (effective.scenario === 'surgery' && effective.surgery.preOpReturn === 'scheduled') {
    const days = toDays(effective.surgery.preOpReturnIn);
    if (!days) return null;
    return {
      appointmentDate: localDateAfterDays(days),
      appointmentType: 'return',
      summary: `retorno antes da cirurgia em ${formatDuration(effective.surgery.preOpReturnIn)}`,
    };
  }

  return null;
}

/** Data prevista de retorno para gravar na evolução ao concluir (desfecho ou campo manual). */
export function resolveEvolutionFollowUpDateForSave(
  outcome: EvolutionOutcome,
  detection: OutcomeDetection,
  followUpDate?: string | null,
): string | null {
  const manual = String(followUpDate || '').trim();
  if (manual) return manual;
  return getOutcomeSchedulingSuggestion(outcome, detection)?.appointmentDate ?? null;
}

// ---------------------------------------------------------------------------
// Cenários e respostas residuais
// ---------------------------------------------------------------------------

/**
 * `unclassified` não é um cenário clínico: significa que houve indicação mas o
 * cadastro não diz a natureza dela, então perguntamos ao médico.
 */
export type OutcomeScenario = 'surgery' | 'exam' | 'clinical' | 'unclassified';

export type SurgeryPreOpReturn = 'none' | 'scheduled';
export type ExamFollowUp = 'back_to_doctor' | 'file_only' | 'external';
export type ClinicalDisposition = 'return' | 'recall' | 'referral' | 'no_recall';

export interface EvolutionOutcome {
  /** Nulo enquanto o médico não decidiu (cenário não classificado). */
  scenario: OutcomeScenario | null;
  /** `manual` quando o médico trocou o cenário detectado. */
  scenarioSource: 'auto' | 'manual';
  surgery: {
    preOpReturn: SurgeryPreOpReturn | null;
    preOpReturnIn: Duration | null;
    preOpReturnReason: string;
    /** Caso urgente: a próxima ação nasce para hoje. */
    timePriority: boolean;
    /** Exames pedidos junto: pré-operatórios (mesmo card) ou diagnósticos (card próprio). */
    examsArePreOp: boolean | null;
    /** Reconvocar caso o paciente não opere. */
    recallIn: Duration | null;
  };
  exam: {
    followUp: ExamFollowUp | null;
    returnIn: Duration | null;
  };
  clinical: {
    disposition: ClinicalDisposition | null;
    returnIn: Duration | null;
    referralTo: string;
  };
  /** Observação livre para a secretaria — não é dado clínico. */
  crmNote: string;
}

/** Desfecho inicial de evoluções novas: atendimento só clínico, sem reconvocação (alta). */
export function defaultEvolutionOutcome(): EvolutionOutcome {
  return {
    scenario: null,
    scenarioSource: 'auto',
    surgery: {
      preOpReturn: null,
      preOpReturnIn: null,
      preOpReturnReason: '',
      timePriority: false,
      examsArePreOp: null,
      recallIn: null,
    },
    exam: { followUp: null, returnIn: null },
    clinical: { disposition: 'no_recall', returnIn: null, referralTo: '' },
    crmNote: '',
  };
}

// ---------------------------------------------------------------------------
// Detecção
// ---------------------------------------------------------------------------

export interface OutcomeDetectionInput {
  indicationItems: EvolutionIndicationItem[];
  printables: EvolutionAttachedPrintable[];
}

export interface OutcomeDetection {
  scenario: OutcomeScenario;
  /** Indicações que levam ao bloco cirúrgico ou à sala de procedimento. */
  surgeryItems: EvolutionIndicationItem[];
  /** Indicações cuja natureza é exame. */
  examItems: EvolutionIndicationItem[];
  /** Indicações sem natureza declarada no cadastro. */
  unclassifiedItems: EvolutionIndicationItem[];
  /** Houve pedido de exame no assistente de impressos. */
  hasExamOrder: boolean;
  examOrderNames: string[];
}

const SURGERY_ACT_TYPES: ClinicalActType[] = ['surgery', 'procedure'];

export function detectOutcomeScenario(input: OutcomeDetectionInput): OutcomeDetection {
  const items = input.indicationItems || [];
  const surgeryItems = items.filter((i) => i.act_type && SURGERY_ACT_TYPES.includes(i.act_type));
  const examItems = items.filter((i) => i.act_type === 'exam');
  const unclassifiedItems = items.filter((i) => !i.act_type);

  const examOrders = (input.printables || []).filter((p) => p.category === 'exam_order');
  const hasExamOrder = examOrders.length > 0;

  // Precedência: cirurgia é inequívoca; indicação sem classificação vem antes de
  // exame/clínico porque poderia ser cirurgia e não temos como saber.
  let scenario: OutcomeScenario;
  if (surgeryItems.length > 0) scenario = 'surgery';
  else if (unclassifiedItems.length > 0) scenario = 'unclassified';
  else if (hasExamOrder || examItems.length > 0) scenario = 'exam';
  else scenario = 'clinical';

  return {
    scenario,
    surgeryItems,
    examItems,
    unclassifiedItems,
    hasExamOrder,
    examOrderNames: examOrders.map((p) => p.templateName).filter(Boolean),
  };
}

/**
 * Aplica o cenário detectado ao desfecho, a menos que o médico tenha escolhido
 * outro explicitamente. É o desfecho "de verdade" — o que valida e vira card.
 * Centralizado aqui para a UI, o gate de conclusão e o payload nunca divergirem.
 */
export function resolveEffectiveOutcome(
  outcome: EvolutionOutcome,
  detection: OutcomeDetection,
): EvolutionOutcome {
  const scenario =
    outcome.scenarioSource === 'manual' && outcome.scenario ? outcome.scenario : detection.scenario;
  return { ...outcome, scenario: scenario === 'unclassified' ? null : scenario };
}

/** Cenário A com exames pedidos junto: precisamos saber se são pré-operatórios. */
export function needsPreOpExamQuestion(
  outcome: EvolutionOutcome,
  detection: OutcomeDetection,
): boolean {
  return outcome.scenario === 'surgery' && (detection.hasExamOrder || detection.examItems.length > 0);
}

// ---------------------------------------------------------------------------
// Validação — o que bloqueia o "Concluir"
// ---------------------------------------------------------------------------

/**
 * Retorna a pendência que impede concluir, ou null quando está tudo respondido.
 * Exame sem destino definido é paciente perdido, por isso é bloqueante.
 */
export function validateOutcome(
  outcome: EvolutionOutcome,
  detection: OutcomeDetection,
): string | null {
  if (!outcome.scenario || outcome.scenario === 'unclassified') {
    return 'Defina o desfecho do atendimento antes de concluir.';
  }

  if (outcome.scenario === 'surgery') {
    if (!outcome.surgery.preOpReturn) {
      return 'Informe se haverá retorno antes da cirurgia.';
    }
    if (outcome.surgery.preOpReturn === 'scheduled' && !toDays(outcome.surgery.preOpReturnIn)) {
      return 'Informe em quanto tempo será o retorno antes da cirurgia.';
    }
    if (needsPreOpExamQuestion(outcome, detection) && outcome.surgery.examsArePreOp === null) {
      return 'Informe se os exames solicitados são pré-operatórios.';
    }
    return null;
  }

  if (outcome.scenario === 'exam') {
    if (!outcome.exam.followUp) {
      return 'Informe o que acontece depois do exame.';
    }
    if (outcome.exam.followUp === 'back_to_doctor' && !toDays(outcome.exam.returnIn)) {
      return 'Informe em quanto tempo o paciente retorna com o exame.';
    }
    return null;
  }

  const { disposition, returnIn, referralTo } = outcome.clinical;
  if (!disposition) return 'Escolha o desfecho clínico do atendimento.';
  if ((disposition === 'return' || disposition === 'recall') && !toDays(returnIn)) {
    return disposition === 'return'
      ? 'Informe em quanto tempo será o retorno.'
      : 'Informe em quanto tempo o paciente deve ser reconvocado.';
  }
  if (disposition === 'referral' && !referralTo.trim()) {
    return 'Informe para quem o paciente foi encaminhado.';
  }
  return null;
}

// ---------------------------------------------------------------------------
// Tradução para CRM
// ---------------------------------------------------------------------------

export type OutcomeFunnel = 'cirurgias' | 'exames' | 'retornos' | 'recalls';

/** Tipos que o board do CRM sabe exibir e editar (CrmBoard.NEXT_ACTION_TYPES). */
export type CrmNextActionType = 'ligar' | 'whatsapp' | 'agendar' | 'orcamento' | 'retorno' | 'outro';

export interface CrmCardPlan {
  funnel_type: OutcomeFunnel;
  /** Etapa desejada pelo nome; cai na primeira do funil se não existir. */
  stage_hint?: string;
  /** Vocabulário do board: ligar | whatsapp | agendar | orcamento | retorno | outro. */
  next_action_type?: CrmNextActionType;
  next_action_note: string;
  next_action_at?: string;
  visible_after?: string;
  notes?: string;
  metadata?: Record<string, any>;
}

export const OUTCOME_FUNNEL_LABEL: Record<OutcomeFunnel, string> = {
  cirurgias: 'Cirurgias',
  exames: 'Exames',
  retornos: 'Retornos',
  recalls: 'Recalls',
};

/** Sem prazo definido o card nasce com ação em 2 dias, para não sumir do Painel do dia. */
const DEFAULT_ACTION_DAYS = 2;
/** Conferir se o exame foi realizado. */
const EXAM_CHECK_DAYS = 7;
/** Exame fora da clínica depende do paciente agendar, então damos mais folga. */
const EXTERNAL_EXAM_CHECK_DAYS = 15;
/** Confirmar que o encaminhamento foi atendido. */
const REFERRAL_CHECK_DAYS = 7;

function itemNames(items: EvolutionIndicationItem[]): string {
  return items.map((i) => i.name).join(', ');
}

/**
 * Exames do atendimento, vindos das duas origens possíveis: o assistente de
 * impressos (pedido de exame) e a grade de indicações (item de categoria exame).
 * Tratar só a primeira deixaria o checklist de pré-op vazio quando o médico
 * indicou o exame pela grade.
 */
function examLabels(detection: OutcomeDetection): string[] {
  const labels = [...detection.examOrderNames, ...detection.examItems.map((i) => i.name)];
  return Array.from(new Set(labels.filter(Boolean)));
}

/**
 * Traduz o desfecho em cards de CRM, o primeiro sendo o principal. Devolve lista
 * vazia quando o desfecho é alta sem reconvocação — nesse caso não há nada a
 * perseguir e criar card seria ruído para a secretaria.
 */
export function resolveCrmPlan(
  outcome: EvolutionOutcome,
  detection: OutcomeDetection,
): CrmCardPlan[] {
  const plans: CrmCardPlan[] = [];
  const note = outcome.crmNote.trim() || undefined;

  if (outcome.scenario === 'surgery') {
    const surgeryLabel = itemNames(detection.surgeryItems) || 'Procedimento indicado';
    const preOpDays = toDays(outcome.surgery.preOpReturnIn);
    const examsArePreOp = outcome.surgery.examsArePreOp === true;
    const exams = examLabels(detection);

    plans.push({
      funnel_type: 'cirurgias',
      stage_hint: 'Indicada',
      next_action_type: 'orcamento',
      next_action_note: [
        outcome.surgery.timePriority
          ? `Prioridade — ${surgeryLabel}: apresentar plano e agendar`
          : `${surgeryLabel} — apresentar plano e agendar`,
        examsArePreOp && exams.length ? `(pré-op: ${exams.join(', ')})` : '',
      ].filter(Boolean).join(' '),
      next_action_at: outcome.surgery.timePriority
        ? isoAfterDays(0)
        : isoAfterDays(outcome.surgery.preOpReturn === 'scheduled' ? preOpDays : DEFAULT_ACTION_DAYS),
      notes: note,
      metadata: {
        outcome_scenario: 'surgery',
        surgery_items: detection.surgeryItems.map((i) => i.name),
        time_priority: outcome.surgery.timePriority,
        pre_op_return: outcome.surgery.preOpReturn,
        pre_op_return_in_days: preOpDays,
        pre_op_return_reason: outcome.surgery.preOpReturnReason.trim() || null,
        // Exame pré-operatório é etapa da própria cirurgia: vira checklist do card
        // em vez de um segundo card, senão a secretaria persegue duas jornadas.
        pre_op_exams: examsArePreOp ? exams : [],
      },
    });

    if (exams.length > 0 && outcome.surgery.examsArePreOp === false) {
      plans.push({
        funnel_type: 'exames',
        stage_hint: 'Solicitado',
        next_action_type: 'ligar',
        next_action_note: 'Exame diagnóstico solicitado — acompanhar realização',
        next_action_at: isoAfterDays(EXAM_CHECK_DAYS),
        visible_after: isoAfterDays(1),
        metadata: { outcome_scenario: 'exam', exams, from_surgery_visit: true },
      });
    }

    const recallDays = toDays(outcome.surgery.recallIn);
    if (recallDays) {
      plans.push({
        funnel_type: 'recalls',
        stage_hint: 'Aguardando data',
        next_action_type: 'whatsapp',
        next_action_note: 'Recall — reconvocar caso não realize a cirurgia',
        next_action_at: isoAfterDays(recallDays),
        metadata: { outcome_scenario: 'recall', reason: 'surgery_not_performed' },
      });
    }

    return plans;
  }

  if (outcome.scenario === 'exam') {
    const exams = examLabels(detection);
    const followUp = outcome.exam.followUp;
    const returnDays = toDays(outcome.exam.returnIn);

    const byFollowUp: Record<ExamFollowUp, { note: string; days: number | null }> = {
      back_to_doctor: {
        note: 'Exame solicitado — acompanhar realização e agendar o retorno',
        days: returnDays,
      },
      file_only: {
        note: 'Exame solicitado — acompanhar realização e arquivar o resultado',
        days: EXAM_CHECK_DAYS,
      },
      external: {
        note: 'Exame externo — cobrar o resultado com o paciente',
        days: EXTERNAL_EXAM_CHECK_DAYS,
      },
    };
    const config = byFollowUp[followUp || 'file_only'];

    plans.push({
      funnel_type: 'exames',
      stage_hint: 'Solicitado',
      next_action_type: followUp === 'external' ? 'whatsapp' : 'ligar',
      next_action_note: config.note,
      next_action_at: isoAfterDays(config.days ?? EXAM_CHECK_DAYS),
      // Etapa "Solicitado" fica oculta por 24h: cobrar no mesmo dia da consulta
      // é atrito com o paciente.
      visible_after: isoAfterDays(1),
      notes: note,
      metadata: {
        outcome_scenario: 'exam',
        exams,
        after_exam: followUp,
        return_in_days: returnDays,
      },
    });
    return plans;
  }

  if (outcome.scenario === 'clinical') {
    const { disposition, returnIn, referralTo } = outcome.clinical;
    const days = toDays(returnIn);

    if (disposition === 'return') {
      plans.push({
        funnel_type: 'retornos',
        stage_hint: 'Pendente de agendar',
        next_action_type: 'agendar',
        next_action_note: 'Encaixar retorno na agenda',
        next_action_at: isoAfterDays(days ?? DEFAULT_ACTION_DAYS),
        notes: note,
        metadata: { outcome_scenario: 'clinical', disposition, return_in_days: days },
      });
    } else if (disposition === 'recall') {
      plans.push({
        funnel_type: 'recalls',
        stage_hint: 'Aguardando data',
        next_action_type: 'whatsapp',
        next_action_note: 'Recall — reconvocar paciente',
        next_action_at: isoAfterDays(days ?? DEFAULT_ACTION_DAYS),
        notes: note,
        metadata: { outcome_scenario: 'clinical', disposition, return_in_days: days },
      });
    } else if (disposition === 'referral') {
      const target = referralTo.trim();
      plans.push({
        funnel_type: 'retornos',
        stage_hint: 'Pendente de agendar',
        next_action_type: 'ligar',
        next_action_note: `Encaminhado a ${target || 'outro profissional'} — confirmar atendimento`,
        next_action_at: isoAfterDays(REFERRAL_CHECK_DAYS),
        notes: note,
        metadata: { outcome_scenario: 'clinical', disposition, referral_to: target || null },
      });
    }
    // `no_recall`: alta definitiva, nenhum card.
    return plans;
  }

  return plans;
}

/** Resumo do destino para mostrar ao médico antes de concluir. */
export function describeCrmPlan(plans: CrmCardPlan[]): string {
  if (!plans.length) return 'Nenhum card será criado no CRM';
  const labels = plans.map((p) => OUTCOME_FUNNEL_LABEL[p.funnel_type]);
  if (labels.length === 1) return `vai p/ ${labels[0]}`;
  return `vai p/ ${labels.slice(0, -1).join(', ')} e ${labels[labels.length - 1]}`;
}

// ---------------------------------------------------------------------------
// Compatibilidade com o formato antigo
// ---------------------------------------------------------------------------

/**
 * Lê o painel "Retorno" legado (4 booleanos + prazos em dias) como desfecho.
 * Evoluções salvas antes desta mudança precisam reabrir sem perder informação.
 */
export function fromLegacyReturnWorkflow(w?: Partial<EvolutionReturnWorkflow> | null): EvolutionOutcome {
  const outcome = defaultEvolutionOutcome();
  if (!w) return outcome;

  const returnDays = Number(w.returnInDays) || 0;
  const reevalDays = Number(w.reevaluationInDays) || 0;
  const recallDays = Number(w.recallInDays) || 0;
  const days = returnDays > 0 ? returnDays : reevalDays;

  if (w.returnForSurgery) {
    outcome.scenario = 'surgery';
    outcome.surgery.preOpReturn = days > 0 ? 'scheduled' : 'none';
    outcome.surgery.preOpReturnIn = fromDays(days);
    outcome.surgery.recallIn = fromDays(recallDays);
    return outcome;
  }
  if (w.returnWithExams) {
    outcome.scenario = 'exam';
    outcome.exam.followUp = days > 0 ? 'back_to_doctor' : 'file_only';
    outcome.exam.returnIn = fromDays(days);
    return outcome;
  }
  if (w.dischargeWithRecall) {
    outcome.scenario = 'clinical';
    outcome.clinical.disposition = 'recall';
    outcome.clinical.returnIn = fromDays(recallDays || days);
    return outcome;
  }
  if (w.dischargeNoRecall) {
    outcome.scenario = 'clinical';
    outcome.clinical.disposition = 'no_recall';
    return outcome;
  }
  if (days > 0) {
    outcome.scenario = 'clinical';
    outcome.clinical.disposition = 'return';
    outcome.clinical.returnIn = fromDays(days);
  }
  return outcome;
}

/**
 * Mantém `metadata.evolution_workflow.return` preenchido no formato antigo, para
 * telas de visualização e relatórios que ainda leem de lá.
 */
export function toLegacyReturnWorkflow(outcome: EvolutionOutcome): EvolutionReturnWorkflow {
  const legacy: EvolutionReturnWorkflow = {
    returnWithExams: false,
    returnForSurgery: false,
    returnInDays: '',
    reevaluationInDays: '',
    dischargeNoRecall: false,
    dischargeWithRecall: false,
    recallInDays: '',
  };
  const asDays = (d: Duration | null) => {
    const days = toDays(d);
    return days ? String(days) : '';
  };

  if (outcome.scenario === 'surgery') {
    legacy.returnForSurgery = true;
    legacy.returnInDays = asDays(outcome.surgery.preOpReturnIn);
    legacy.recallInDays = asDays(outcome.surgery.recallIn);
    return legacy;
  }
  if (outcome.scenario === 'exam') {
    legacy.returnWithExams = true;
    legacy.returnInDays = asDays(outcome.exam.returnIn);
    return legacy;
  }
  if (outcome.scenario === 'clinical') {
    const { disposition } = outcome.clinical;
    if (disposition === 'recall') {
      legacy.dischargeWithRecall = true;
      legacy.recallInDays = asDays(outcome.clinical.returnIn);
    } else if (disposition === 'no_recall') {
      legacy.dischargeNoRecall = true;
    } else if (disposition === 'return' || disposition === 'referral') {
      legacy.returnInDays = asDays(outcome.clinical.returnIn);
    }
  }
  return legacy;
}

/** Reabre um desfecho já salvo em `metadata.evolution_workflow.outcome`. */
export function parseSavedOutcome(raw: any): EvolutionOutcome | null {
  if (!raw || typeof raw !== 'object') return null;
  const base = defaultEvolutionOutcome();
  return {
    ...base,
    ...raw,
    surgery: { ...base.surgery, ...(raw.surgery || {}) },
    exam: { ...base.exam, ...(raw.exam || {}) },
    clinical: { ...base.clinical, ...(raw.clinical || {}) },
  };
}
